import { AlertTriangle, CheckCircle2, CircleDashed, LoaderCircle, RefreshCw, Search, Send, Sparkles, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";

import { api } from "@/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PipelinePanel } from "@/components/PipelinePanel";
import { BackfillPanel } from "@/components/BackfillPanel";
import { StatusBadge } from "@/components/StatusBadge";

const initialDashboard = {
  in_progress: 0,
  completed_today: 0,
  to_review: 0,
  recent: [],
};

function formatDateTime(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

function latestAutoQuote(history) {
  const auto = (history || []).filter((entry) => entry.sent_automatically);
  if (auto.length === 0) return null;
  return auto[auto.length - 1];
}

function isDedupeEntry(entry) {
  return entry && entry.status === "deja_envoye";
}

const RECENT_WINDOW_MS = 10000;

function isRecent(item, now) {
  const timestamp = item.updated_at || item.created_at;
  if (!timestamp) return false;
  const parsed = Date.parse(timestamp);
  if (Number.isNaN(parsed)) return false;
  return now - parsed < RECENT_WINDOW_MS;
}

const PAGE_SIZE = 200;

export default function ActivityPage() {
  const [dashboard, setDashboard] = useState(initialDashboard);
  const [loading, setLoading] = useState(true);
  const [todayOnly, setTodayOnly] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [resubmittingId, setResubmittingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resubmitProgress, setResubmitProgress] = useState(null);
  const [bulkResubmitting, setBulkResubmitting] = useState(false);
  const recentLenRef = useRef(0);

  const toggleSelected = (treatmentId) => {
    setSelectedIds((current) => (current.includes(treatmentId) ? current.filter((id) => id !== treatmentId) : [...current, treatmentId]));
  };

  const toggleAllVisible = (rows) => {
    const ids = rows.filter((item) => item.status !== "en_cours").map((item) => item.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.includes(id));
    setSelectedIds((current) => (allSelected ? current.filter((id) => !ids.includes(id)) : [...new Set([...current, ...ids])]));
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const { data } = pendingDelete.ids.length === 1
        ? await api.delete(`/treatments/${pendingDelete.ids[0]}`)
        : await api.post("/treatments/bulk-delete", { treatment_ids: pendingDelete.ids });
      toast.success(data.message);
      setSelectedIds((current) => current.filter((id) => !pendingDelete.ids.includes(id)));
      setPendingDelete(null);
      await loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "La suppression a échoué.");
    } finally {
      setDeleting(false);
    }
  };

  const resubmit = async (treatmentId) => {
    setResubmittingId(treatmentId);
    try {
      const { data } = await api.post(`/treatments/${treatmentId}/resubmit`);
      toast.success(data.message);
      await loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "La resoumission a échoué.");
    } finally {
      setResubmittingId(null);
    }
  };

  const loadResubmitProgress = async () => {
    try {
      const { data } = await api.get("/treatments/resubmit-progress");
      setResubmitProgress(data.batch);
    } catch {
      /* silencieux */
    }
  };

  const bulkResubmit = async () => {
    setBulkResubmitting(true);
    try {
      const { data } = await api.post("/treatments/bulk-resubmit");
      toast.success(data.message);
      await Promise.all([loadDashboard(), loadResubmitProgress()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "La relance groupée a échoué.");
    } finally {
      setBulkResubmitting(false);
    }
  };

  useEffect(() => {
    recentLenRef.current = dashboard.recent.length;
  }, [dashboard.recent.length]);

  const loadDashboard = async () => {
    try {
      const limit = recentLenRef.current > PAGE_SIZE ? recentLenRef.current : PAGE_SIZE;
      const params = { today_only: todayOnly, limit, offset: 0 };
      if (debouncedQuery) params.q = debouncedQuery;
      const { data } = await api.get("/dashboard", { params });
      setDashboard(data);
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardRef = useRef(loadDashboard);
  loadDashboardRef.current = loadDashboard;

  const loadMore = async () => {
    if (!dashboard.has_more || loadingMore) return;
    setLoadingMore(true);
    try {
      const params = { today_only: todayOnly, limit: PAGE_SIZE, offset: dashboard.recent.length };
      if (debouncedQuery) params.q = debouncedQuery;
      const { data } = await api.get("/dashboard", { params });
      setDashboard((current) => ({
        ...data,
        recent: [...current.recent, ...data.recent],
      }));
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(searchQuery.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    loadResubmitProgress();
  }, []);

  const resubmitRunning = resubmitProgress && resubmitProgress.status === "en_cours";

  useEffect(() => {
    if (!resubmitRunning) return undefined;
    const timer = setInterval(() => {
      loadResubmitProgress();
      loadDashboardRef.current();
    }, 2000);
    return () => clearInterval(timer);
  }, [resubmitRunning]);

  const hasActiveOrRecent = useMemo(
    () => dashboard.recent.some((item) => item.status === "en_cours" || isRecent(item, nowMs)),
    [dashboard.recent, nowMs],
  );

  const filteredRecent = dashboard.recent;

  useEffect(() => {
    loadDashboardRef.current();
    const timer = setInterval(() => {
      loadDashboardRef.current();
      setNowMs(Date.now());
    }, hasActiveOrRecent ? 700 : 2000);
    return () => clearInterval(timer);
  }, [todayOnly, hasActiveOrRecent, debouncedQuery]);

  const stats = [
    { label: "Traitements en cours", value: dashboard.in_progress, icon: CircleDashed },
    { label: "Fiches enrichies aujourd’hui", value: dashboard.completed_today, icon: CheckCircle2 },
    { label: "Fiches à vérifier", value: dashboard.to_review, icon: AlertTriangle },
  ];

  return (
    <section className="page" data-testid="activity-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">SUIVI OPÉRATIONNEL</p>
          <h1 data-testid="activity-page-title">Tableau d’activité</h1>
          <p className="page-subtitle" data-testid="activity-page-subtitle">Les traitements se mettent à jour automatiquement.</p>
        </div>
        <button className="icon-action" type="button" onClick={loadDashboard} disabled={loading} data-testid="refresh-activity-button" aria-label="Actualiser l’activité">
          <RefreshCw size={18} className={loading ? "spinning" : ""} />
        </button>
      </header>

      <PipelinePanel compact />
      <div className="stats-grid">
        {stats.map(({ label, value, icon: Icon }) => (
          <article className="stat-box" key={label} data-testid={`stat-${label.toLowerCase().replaceAll(" ", "-").replace("’", "-")}`}>
            <Icon size={19} />
            <span>{label}</span>
            <strong
              data-testid={
                label === "Traitements en cours"
                  ? "in-progress-count"
                  : label === "Fiches enrichies aujourd’hui"
                    ? "completed-today-count"
                    : "to-review-count"
              }
            >
              {value}
            </strong>
          </article>
        ))}
      </div>
      {(() => {
        const retryable = (dashboard.errors || 0) + (dashboard.to_review || 0);
        return (
          <section className="bulk-retry-panel" data-testid="bulk-resubmit-panel">
            <div className="bulk-retry-head">
              <div>
                <p className="eyebrow">RELANCE GROUPÉE</p>
                <p className="bulk-retry-hint" data-testid="bulk-resubmit-hint">
                  {retryable > 0
                    ? `${retryable} fiche(s) en « Erreur » ou « À vérifier » peuvent être relancées d’un coup, en arrière-plan (une par une, avec une courte pause pour ménager les API publiques).`
                    : "Aucune fiche en « Erreur » ou « À vérifier » à relancer pour le moment."}
                </p>
              </div>
              <button
                type="button"
                className="secondary-button"
                onClick={bulkResubmit}
                disabled={bulkResubmitting || resubmitRunning || retryable === 0}
                data-testid="bulk-resubmit-button"
              >
                <Send size={16} />
                {bulkResubmitting ? "Lancement…" : resubmitRunning ? "Relance en cours…" : `Relancer toutes les fiches en erreur${retryable > 0 ? ` (${retryable})` : ""}`}
              </button>
            </div>
            {resubmitProgress && resubmitProgress.status === "en_cours" && (
              <div className="retry-progress" data-testid="resubmit-progress-banner">
                <div className="retry-progress-track"><div className="retry-progress-fill" style={{ width: `${resubmitProgress.total ? Math.round((resubmitProgress.done / resubmitProgress.total) * 100) : 0}%` }} /></div>
                <p data-testid="resubmit-progress-text">
                  Relance en cours : <strong>{resubmitProgress.done}/{resubmitProgress.total}</strong> traités — {resubmitProgress.success} réussie{resubmitProgress.success > 1 ? "s" : ""}, {resubmitProgress.failed} échec{resubmitProgress.failed > 1 ? "s" : ""} · mise à jour automatique
                </p>
              </div>
            )}
            {resubmitProgress && resubmitProgress.status === "termine" && Date.now() - new Date(resubmitProgress.finished_at).getTime() < 15 * 60 * 1000 && (
              <p className="retry-progress-done" data-testid="resubmit-progress-done">
                Dernière relance groupée terminée ({formatDateTime(resubmitProgress.finished_at)}) : {resubmitProgress.success} réussie{resubmitProgress.success > 1 ? "s" : ""} sur {resubmitProgress.total}, {resubmitProgress.failed} échec{resubmitProgress.failed > 1 ? "s" : ""}.
              </p>
            )}
            {resubmitProgress && resubmitProgress.status === "interrompu" && (
              <p className="review-alert" data-testid="resubmit-progress-interrupted">
                La dernière relance groupée a été interrompue à {resubmitProgress.done}/{resubmitProgress.total}. Les fiches non traitées repassent en « À vérifier » : relancez-les à nouveau.
              </p>
            )}
          </section>
        );
      })()}
      <BackfillPanel />
      <section className="data-panel" data-testid="recent-activity-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">JOURNAL DES COMMUNES</p>
            <h2>Derniers traitements</h2>
          </div>
          <div className="journal-controls">
            <div className="commune-search" data-testid="commune-search-wrap">
              <Search size={15} />
              <input
                type="search"
                placeholder="Rechercher une mairie ou une commune…"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                data-testid="commune-search-input"
              />
            </div>
            <label className="today-toggle">
              <input
                type="checkbox"
                checked={todayOnly}
                onChange={(event) => setTodayOnly(event.target.checked)}
                data-testid="today-only-toggle"
              />
              <span>Aujourd’hui</span>
            </label>
            <span className="table-count" data-testid="recent-treatment-count">
              {filteredRecent.length}{typeof dashboard.total === "number" ? ` / ${dashboard.total}` : ""} ligne(s){searchQuery && ` (filtre actif)`}
            </span>
          </div>
        </div>
        <div className="table-wrap">
          <table data-testid="recent-treatments-table">
            <thead>
              <tr>
                <th className="select-cell">
                  <input
                    type="checkbox"
                    aria-label="Tout sélectionner"
                    checked={filteredRecent.some((item) => item.status !== "en_cours") && filteredRecent.filter((item) => item.status !== "en_cours").every((item) => selectedIds.includes(item.id))}
                    onChange={() => toggleAllVisible(filteredRecent)}
                    data-testid="select-all-treatments"
                  />
                </th>
                <th>Commune</th>
                <th>Soumis le</th>
                <th>INSEE</th>
                <th>Quantité déclarée</th>
                <th>Climatiseurs</th>
                <th>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {!loading && dashboard.recent.length === 0 && <tr><td colSpan="8" className="empty-cell" data-testid="empty-activity-message">Aucun traitement pour le moment.</td></tr>}
              {!loading && dashboard.recent.length > 0 && filteredRecent.length === 0 && <tr><td colSpan="8" className="empty-cell" data-testid="empty-search-message">Aucune commune ne correspond à « {searchQuery} ».</td></tr>}
              {filteredRecent.map((item) => {
                const title = item.original_lead_title || item.commune_name;
                const history = item.quote_history || [];
                const autoQuote = latestAutoQuote(history);
                const dedupeSkipped = isDedupeEntry(autoQuote);
                const autoTooltip = autoQuote
                  ? (dedupeSkipped
                    ? `Devis déjà envoyé le ${formatDateTime(autoQuote.created_at)} à ${autoQuote.recipient_email || "destinataire inconnu"} — nouveau déclenchement ignoré pour éviter le doublon.`
                    : `Envoyé le ${formatDateTime(autoQuote.created_at)} à ${autoQuote.recipient_email || "destinataire inconnu"}`)
                  : "";
                const inProgress = item.status === "en_cours";
                const justFinished = !inProgress && isRecent(item, nowMs);
                const rowClass = inProgress
                  ? "treatment-in-progress"
                  : justFinished
                    ? "treatment-just-finished"
                    : "";

                return (
                  <tr
                    key={item.id}
                    className={`${rowClass}${selectedIds.includes(item.id) ? " row-selected" : ""}`}
                    data-testid={`treatment-row-${item.id}`}
                  >
                    <td className="select-cell">
                      <input
                        type="checkbox"
                        aria-label="Sélectionner cette ligne"
                        checked={selectedIds.includes(item.id)}
                        disabled={inProgress}
                        onChange={() => toggleSelected(item.id)}
                        data-testid={`treatment-select-${item.id}`}
                      />
                    </td>
                    <td>
                      {item.lead_url ? (
                        <a
                          className="lead-link"
                          href={item.lead_url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`nocrm-lead-link-${item.id}`}
                        >
                          {title}
                        </a>
                      ) : (
                        title
                      )}
                      {autoQuote && (
                        <span
                          className={dedupeSkipped ? "auto-quote-badge dedupe" : "auto-quote-badge"}
                          title={autoTooltip}
                          data-testid={`auto-quote-badge-${item.id}`}
                        >
                          <Sparkles size={12} />
                          {dedupeSkipped ? "Devis déjà envoyé (pas de re-envoi)" : "Devis envoyé automatiquement"}
                        </span>
                      )}
                      {item.status === "a_verifier" && item.review_reason && (
                        <div
                          className="review-reason-inline"
                          data-testid={`review-reason-${item.id}`}
                        >
                          <AlertTriangle size={13} />
                          <span>{item.review_reason}</span>
                        </div>
                      )}
                      {item.status === "erreur" && item.review_reason && (
                        <div
                          className="review-reason-inline error"
                          data-testid={`error-reason-${item.id}`}
                        >
                          <AlertTriangle size={13} />
                          <span>{item.review_reason}</span>
                        </div>
                      )}
                      {history.length > 0 && (
                        <div className="quote-history-list" data-testid={`quote-history-${item.id}`}>
                          {history.map((quote, index) => (
                            <small
                              key={`${quote.number}-${quote.version}-${quote.created_at}-${index}`}
                              data-testid={`quote-history-entry-${item.id}-${index}`}
                            >
                              Devis {quote.number} · V{quote.version} · {quote.status} · {formatDateTime(quote.created_at)}
                            </small>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="mono" data-testid={`treatment-date-${item.id}`}>
                      {formatDateTime(item.created_at)}
                    </td>
                    <td className="mono" data-testid={`treatment-insee-${item.id}`}>
                      {item.insee_code || "—"}
                    </td>
                    <td className="mono" data-testid={`treatment-declared-count-${item.id}`}>
                      {item.declared_establishment_count ?? "—"}
                    </td>
                    <td className="mono" data-testid={`treatment-total-quantity-${item.id}`}>
                      {item.declared_establishment_count === undefined || item.declared_establishment_count === null
                        ? "À recalculer"
                        : item.total_quantity}
                    </td>
                    <td>
                      <a
                        className="secondary-button"
                        href={`/devis/validation?tid=${item.id}`}
                        data-testid={`correct-quote-${item.id}`}
                      >
                        Corriger devis
                      </a>
                      {(item.status === "a_verifier" || item.status === "erreur") && (
                        <button
                          className="secondary-button resubmit-button"
                          type="button"
                          disabled={resubmittingId === item.id}
                          onClick={() => resubmit(item.id)}
                          title="Relit la fiche noCRM corrigée (ville, quantité, e-mail), refait l’enrichissement, régénère le devis et l’envoie."
                          data-testid={`resubmit-${item.id}`}
                        >
                          <Send size={14} />
                          {resubmittingId === item.id ? "Resoumission…" : "Resoumettre"}
                        </button>
                      )}
                      {inProgress && (
                        <LoaderCircle
                          className="spinning progress-spinner"
                          size={15}
                          data-testid={`treatment-progress-icon-${item.id}`}
                        />
                      )}
                      {justFinished && (
                        <span
                          className="just-finished-tag"
                          data-testid={`treatment-just-finished-${item.id}`}
                        >
                          Nouveau
                        </span>
                      )}
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="delete-cell">
                      <button
                        type="button"
                        className="icon-action danger-icon"
                        disabled={inProgress}
                        title={inProgress ? "Traitement en cours : suppression impossible" : "Supprimer cette ligne"}
                        aria-label="Supprimer cette ligne"
                        onClick={() => setPendingDelete({ ids: [item.id], label: title })}
                        data-testid={`delete-treatment-${item.id}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {dashboard.has_more && (
          <div className="load-more-row">
            <button
              className="secondary-button"
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              data-testid="load-more-treatments-button"
            >
              {loadingMore ? <LoaderCircle className="spinning" size={16} /> : <RefreshCw size={16} />}
              Charger plus ({dashboard.total - dashboard.recent.length} restantes)
            </button>
          </div>
        )}
      </section>

      {selectedIds.length > 0 && createPortal(
        <div className="bulk-bar" data-testid="activity-bulk-bar">
          <span className="bulk-count" data-testid="activity-bulk-count">
            {selectedIds.length} ligne{selectedIds.length > 1 ? "s" : ""} sélectionnée{selectedIds.length > 1 ? "s" : ""}
          </span>
          <button
            type="button"
            className="danger-button"
            onClick={() => setPendingDelete({ ids: selectedIds, label: selectedIds.length === 1 ? (() => { const row = dashboard.recent.find((item) => item.id === selectedIds[0]); return row?.original_lead_title || row?.commune_name || "cette ligne"; })() : null })}
            data-testid="activity-bulk-delete-button"
          >
            <Trash2 size={14} /> Supprimer
          </button>
          <button type="button" className="bulk-cancel" onClick={() => setSelectedIds([])} data-testid="activity-bulk-cancel-button">
            <X size={14} /> Annuler
          </button>
        </div>,
        document.body,
      )}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        busy={deleting}
        title={pendingDelete?.ids.length === 1 ? `Supprimer le traitement de « ${pendingDelete.label} » ?` : `Supprimer ${pendingDelete?.ids.length} lignes ?`}
        description="Cette action est définitive : la ligne et ses devis PDF sont retirés de l’application. La fiche noCRM et les e-mails déjà envoyés ne sont pas modifiés."
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </section>
  );
}